import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { asCollection } from '../lib/collections'
import { formatDate } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import ListContainer from '../ui/ListContainer'
import MenuGuideButton from '../components/MenuGuideButton'

export default function BannersPage() {
    const { hasAnyRole } = useAuth()
    const { show } = useToast()
    const queryClient = useQueryClient()
    const [searchParams, setSearchParams] = useSearchParams()
    const [page, setPage] = useState(Number(searchParams.get('page') || '1'))
    const [size] = useState(Number(searchParams.get('size') || '20'))
    const [enabledFilter, setEnabledFilter] = useState(searchParams.get('enabled') || '')

    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false

    // URL 동기화
    React.useEffect(() => {
        const next = new URLSearchParams()
        if (page && page !== 1) next.set('page', String(page))
        if (size && size !== 20) next.set('size', String(size))
        if (enabledFilter) next.set('enabled', enabledFilter)
        setSearchParams(next, { replace: true })
    }, [page, size, enabledFilter, setSearchParams])

    const listQuery = useQuery({
        queryKey: ['banners', { page, size, enabled: enabledFilter }],
        queryFn: async () => {
            const params = {}
            params.page = page - 1
            params.size = size
            if (enabledFilter === 'true') params.enabled = true
            if (enabledFilter === 'false') params.enabled = false
            const res = await api.get('/banners', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const raw = asCollection(payload)
            const totalElements = Number(payload?.totalElements ?? payload?.total ?? raw.length ?? 0)
            return { items: raw, total: totalElements }
        },
        enabled: isOperator,
        keepPreviousData: true,
    })

    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            await api.delete(`/banners/${id}`)
        },
        onSuccess: () => {
            show('삭제되었습니다', 'success')
            queryClient.invalidateQueries(['banners'])
        },
        onError: () => {
            show('삭제에 실패했습니다', 'error')
        },
    })

    const toggleEnabledMutation = useMutation({
        mutationFn: async ({ id, enabled }) => {
            await api.put(`/banners/${id}`, { enabled: !enabled })
        },
        onSuccess: () => {
            show('변경되었습니다', 'success')
            queryClient.invalidateQueries(['banners'])
        },
        onError: () => {
            show('변경에 실패했습니다', 'error')
        },
    })

    const items = listQuery.data?.items || []
    const total = listQuery.data?.total || 0

    const handleDelete = async (e, bannerId) => {
        e.stopPropagation()
        if (!window.confirm('정말 삭제하시겠습니까?')) return
        deleteMutation.mutate(bannerId)
    }

    const handleToggleEnabled = async (e, banner) => {
        e.stopPropagation()
        toggleEnabledMutation.mutate({ id: banner.id, enabled: banner.enabled })
    }

    const header = (
        <div className="p-4 border-b bg-white">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold">배너 관리</h1>
                    <MenuGuideButton slug="ops-management" title="운영관리 가이드" />
                </div>
                {isOperator && (
                    <Link
                        to="/ops/banners/new"
                        className="inline-flex items-center gap-2 rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                        </svg>
                        작성
                    </Link>
                )}
            </div>
            <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-slate-700">활성화:</label>
                <select
                    value={enabledFilter}
                    onChange={(e) => {
                        setEnabledFilter(e.target.value)
                        setPage(1)
                    }}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                >
                    <option value="">전체</option>
                    <option value="true">활성화</option>
                    <option value="false">비활성화</option>
                </select>
            </div>
        </div>
    )

    const table = items.length > 0 ? (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-2 text-left">제목</th>
                    <th className="px-4 py-2 text-left">이미지</th>
                    <th className="px-4 py-2 text-left">링크</th>
                    <th className="px-4 py-2 text-left">활성화</th>
                    <th className="px-4 py-2 text-left">우선순위</th>
                    <th className="px-4 py-2 text-left">기간</th>
                    <th className="px-4 py-2 text-left">작성일</th>
                    {isOperator && (
                        <th className="px-4 py-2 text-right">관리</th>
                    )}
                </tr>
            </thead>
            <tbody>
                {items.map((banner) => (
                    <tr key={banner.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-2">
                            <div className="font-medium">{banner.title || '(제목 없음)'}</div>
                            {banner.description && (
                                <div className="text-xs text-slate-500 mt-1 line-clamp-1">{banner.description}</div>
                            )}
                        </td>
                        <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                                {banner.imageFileIdPc && (
                                    <span className="text-xs text-slate-600">PC</span>
                                )}
                                {banner.imageFileIdMobile && (
                                    <span className="text-xs text-slate-600">Mobile</span>
                                )}
                                {banner.imageFileIdApp && (
                                    <span className="text-xs text-slate-600">APP</span>
                                )}
                            </div>
                        </td>
                        <td className="px-4 py-2">
                            {banner.targetUrl ? (
                                <a
                                    href={banner.targetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-brand-sky hover:underline truncate max-w-xs block"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {banner.targetUrl}
                                </a>
                            ) : (
                                <span className="text-xs text-slate-400">-</span>
                            )}
                        </td>
                        <td className="px-4 py-2">
                            <button
                                type="button"
                                onClick={(e) => handleToggleEnabled(e, banner)}
                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                    banner.enabled
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-slate-100 text-slate-800'
                                }`}
                            >
                                {banner.enabled ? '활성' : '비활성'}
                            </button>
                        </td>
                        <td className="px-4 py-2">{banner.priority || 0}</td>
                        <td className="px-4 py-2 text-xs">
                            {banner.startDate || banner.endDate ? (
                                <div>
                                    {banner.startDate ? formatDate(banner.startDate) : '무제한'} ~{' '}
                                    {banner.endDate ? formatDate(banner.endDate) : '무제한'}
                                </div>
                            ) : (
                                <span className="text-slate-400">무제한</span>
                            )}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">
                            {banner.createdAt ? formatDate(banner.createdAt) : '-'}
                        </td>
                        {isOperator && (
                            <td className="px-4 py-2 text-right">
                                <div className="flex items-center justify-end gap-2">
                                    <Link
                                        to={`/ops/banners/${banner.id}/edit`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-xs text-brand-sky hover:underline"
                                    >
                                        수정
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={(e) => handleDelete(e, banner.id)}
                                        className="text-xs text-red-600 hover:underline"
                                    >
                                        삭제
                                    </button>
                                </div>
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    ) : (
        <div className="p-8 text-center text-slate-500">
            {listQuery.isLoading ? '로딩 중...' : '등록된 배너가 없습니다.'}
        </div>
    )

    const footer = total > 0 ? (
        <div className="p-4 border-t bg-white flex items-center justify-between">
            <div className="text-sm text-slate-600">
                총 {total}개 중 {items.length}개 표시
            </div>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="rounded border px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    이전
                </button>
                <span className="text-sm text-slate-600">
                    {page} / {Math.ceil(total / size)}
                </span>
                <button
                    type="button"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(total / size)}
                    className="rounded border px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    다음
                </button>
            </div>
        </div>
    ) : null

    if (!isOperator) {
        return (
            <div className="p-8 text-center text-slate-500">
                접근 권한이 없습니다.
            </div>
        )
    }

    return (
        <ListContainer header={header} table={table} footer={footer} />
    )
}
